import {
  useContentful,
  useDoctorProfile,
  usePromotionList,
} from 'reactQuery/queries';
import { LoadingBar, Notification } from 'components/core';
import { GlobalErrorFallback } from 'components/GlobalErrorFallback';
import {
  isRedeemEligibleUser,
  shouldDisplayContentfulContentByCountry,
  shouldDisplayContentfulContentByRegion,
} from 'components/ContentfulHomePage/ContentfulHomePage.helper';
import { HeroBanner } from 'components/HeroBanner';
import { useI18n } from 'contexts/i18n';
import { ContentfulProductCarousel } from 'components/ContentfulHomePage/index';
import { Promotions } from 'components/Promotions';
import { Link } from 'styles/sharedStyles';
import getConfig from 'next/config';
import { useWindowWidth } from 'utils/hooks';

const ContentfulHomePage = () => {
  const { data: contentful, isLoading, isError } = useContentful();
  const { data: doctorProfile } = useDoctorProfile();
  const { data: promotionList } = usePromotionList();

  const homePageData = contentful?.homePageLanding.filter(
    homePage =>
      shouldDisplayContentfulContentByRegion(homePage.targetingRegion) &&
      homePage
  )[0];

  const redeemEligibleUser = isRedeemEligibleUser(promotionList);

  const { publicRuntimeConfig } = getConfig();
  const storeRegion = publicRuntimeConfig.STORE_REGION;

  const i18n = useI18n();

  const fullWidth = useWindowWidth();

  if (isLoading) {
    return <LoadingBar />;
  }
  if (isError) {
    return <GlobalErrorFallback />;
  }

  return (
    <>
      {storeRegion === 'APAC' && redeemEligibleUser && (
        <Notification
          title={i18n['Reward Points:']}
          type="info"
          isMultiLine={false}
          marginTop="10px"
          marginBottom="10px"
          showCloseButton={true}
          fullWidth
        >
          {i18n['To check available Rewards points,']}{' '}
          <Link
            href={publicRuntimeConfig.REDIRECT_REWARD_POINTS_URL}
            target={'_blank'}
          >
            {i18n['Click Here']}
          </Link>
        </Notification>
      )}

      {homePageData
        ? homePageData.contents?.map(section => {
            const productCarousel = section.id === 'productCarousel';
            const promotionBlock = section.id === 'promotionBlock';
            const heroSection = section.id === 'heroSection';

            if (productCarousel) {
              const filteredSkuList = section.products
                .filter(
                  product =>
                    shouldDisplayContentfulContentByCountry(
                      product.targetingCountries,
                      doctorProfile
                    ) && product
                )
                .map(product => product.sku);

              return (
                <ContentfulProductCarousel
                  key={section.id}
                  title={section.title}
                  skus={filteredSkuList}
                  promotionList={promotionList}
                />
              );
            }

            if (promotionBlock) {
              const filteredCards = section.cards.filter(
                card =>
                  shouldDisplayContentfulContentByCountry(
                    card.targetingCountries,
                    doctorProfile
                  ) && card
              );
              const size = filteredCards.length > 2 ? 'small' : 'large';

              return (
                <Promotions
                  key={section.id}
                  promotedTaxons={filteredCards}
                  size={size}
                />
              );
            }

            if (heroSection) {
              const filteredBanners = section.slides.filter(
                banner =>
                  shouldDisplayContentfulContentByCountry(
                    banner.targetingCountries,
                    doctorProfile
                  ) && banner
              );

              return (
                <HeroBanner
                  key={section.id}
                  bannerSlides={filteredBanners}
                  isLoading={isLoading}
                  isError={isError}
                />
              );
            }
          })
        : null}
    </>
  );
};

export default ContentfulHomePage;
